/**
 * OPT-C1 · 产品多选项（今日版）API 客户端。
 * 唯一契约：designs/mockups/products/API_CONTRACT-产品多选项-今日版.md（改动先找架构师）。
 *
 * 今日版：同一产品下多个选项，共用产品的团期 / 车 / 人数上限；选项可不同 = 名称 / 编号 / 状态 / 默认 + 上车与接送 + 讲解语言 +
 * 餐标 / 已含门票 / 城际用车 + 平台连接。overrides 里 null = 跟随产品；PATCH 只提交传了的键，传 null = 恢复跟随。
 */
import { api } from "./api";
import type { DropOffStop, PickupStop } from "./types/products";
import type { IntercityLegIn, IntercityLegRead } from "./types/products";

export type OptionStatus = "active" | "inactive";

/** 选项自己的覆盖值：null = 跟随产品。 */
export interface OptionOverrides {
  pickup_stops: PickupStop[] | null;
  drop_off_stops: DropOffStop[] | null;
  guide_languages: string[] | null;
  lunch_cost_per_person_cents: number | null;
  /** {景点 id: 票种 id}；只覆盖产品里「已含」的景点 */
  included_tickets: Record<string, string> | null;
  /** 整列表替换产品的城际用车段 */
  intercity_legs: IntercityLegIn[] | null;
  // OPT-C2 / B3a · 选项自己的人数 / 出发时间 / 开团规律 / 车 / 车队成本（全部可空，null = 跟随产品；默认选项不收）。
  // OPT-B3c · 每单最少 / 最多人数、截单小时已生效（默认选项也能设这 3 项；null = 留空 = 默认：最少 1 / 最多 = 每团上限 / 不额外截单）。🔴 is_private 仍只存不生效，界面不显示
  min_pax_per_booking?: number | null;
  max_pax_per_booking?: number | null;
  cutoff_hours?: number | null;
  max_pax_per_departure?: number | null;
  min_pax?: number | null;
  start_time?: string | null;                        // "HH:MM" 24h
  weekly_cadence_json?: { days_of_week: number[] } | null;
  allowed_vehicle_models?: string[] | null;
  allowed_vehicle_ids?: string[] | null;
  default_vehicle_id?: string | null;
  fleet_daily_cost_override_cents?: number | null;
}

/** 生效值（选项自己的 ?? 产品的）。included_tickets / intercity_legs 带名称和价格，形状以后端实际返回为准，前端读取时容错。 */
export interface OptionEffective {
  pickup_stops: PickupStop[] | null;
  drop_off_stops: DropOffStop[] | null;
  guide_languages: string[] | null;
  lunch_cost_per_person_cents: number | null;
  included_tickets: unknown;
  intercity_legs: IntercityLegRead[] | null;
  min_pax_per_booking?: number | null;
  max_pax_per_booking?: number | null;
  cutoff_hours?: number | null;
  max_pax_per_departure?: number | null;
  min_pax?: number | null;
  start_time?: string | null;
  weekly_cadence_json?: { days_of_week: number[] } | null;
  allowed_vehicle_models?: string[] | null;
  allowed_vehicle_ids?: string[] | null;
  default_vehicle_id?: string | null;
  fleet_daily_cost_override_cents?: number | null;
}

export interface OptionMapping {
  platform_code: string;
  external_product_id: string;
  occupancy: number | null;
  option_kind: string;       // regular | extra_vehicle（加售 -X）
  status: string;            // active | inactive
  platform_bound: boolean;
}

export interface ProductOption {
  id: string;
  code: string;
  letter: string;
  name_cn: string;
  name_en: string | null;
  is_default: boolean;
  status: OptionStatus;
  sort_order: number;
  overrides: OptionOverrides;
  effective: OptionEffective;
  mappings: OptionMapping[];
  order_count: number;
  /** OPTPRICE-1 · 选项分价小结（卡片标签用）：金额只有成本查看权限才带；默认选项 / 门票产品没有 */
  pricing_summary?: OptionPricingSummary;
  /** TICKETOPT-1 · 门票选项关联的票种（非门票产品恒 null） */
  ticket_type_id?: string | null;
  ticket_type_name_cn?: string | null;
  /** TICKETOPT-1 · 门票选项游玩时长（分钟，选填） */
  visit_minutes?: number | null;
}

export interface OptionPricingSummary {
  is_default: boolean;
  mode: "same" | "own";
  fixed_channels: number;
  follow_channels: number;
  b2b_settle_cents?: number;
  b2b_list_cents?: number;
}

/** OPTPRICE-1 · 选项分价（编辑选项「售价」一节）。每个格子：own_cents 本选项填的（空 = 跟产品）/ effective_cents 现在生效 / product_cents 产品价参考 / source。 */
export interface OptionPricingCell { own_cents: number | null; effective_cents: number | null; product_cents: number | null; source: "option" | "product" | "fixed" | "auto" }   // 第一个选项：fixed 固定 / auto 按加成率自动；其它选项：option 单独定价 / product 跟第一个
export interface OptionPricingRow { key: string; label: string; channel: string; cells: Record<string, OptionPricingCell> }
export interface OptionPricingView {
  is_default: boolean;
  first?: boolean;                      // PRICEUNIFY-1 · 第一个选项：录产品级价，没有「同价 / 单独」切换
  mode: "same" | "own";
  multi: boolean;
  occupancies: number[];
  keys: string[];                       // 一日游 ["all"]；多日游 ["1","2","3","4"]（人/房）；门票选项且票种有儿童成本 ["all","child"]
  is_ticket?: boolean;                  // TICKETOPT-1
  child_enabled?: boolean;              // TICKETOPT-1 · 票种有儿童成本才有儿童格
  rows: OptionPricingRow[];
  cost_cents: Record<string, number> | null;   // 仅成本查看权限
  margin: Record<string, { cents: number; pct: number } | null> | null;
  note?: string;
}
export interface SaveOptionPricingBody {
  mode: "same" | "own";
  /** {b2b_settle|b2b_list|direct|social|api: {all | 1..4: 每人金额（分）| null = 跟产品}} */
  cells?: Record<string, Record<string, number | null>>;
  note?: string;
  /** PRICEUNIFY-1 · 第一个（默认）选项保存 = 改产品级价，REASON-1 原因代码（price_change） */
  reason_code?: string;
}

export interface CreateOptionBody {
  name_cn: string;
  name_en: string;
  /** 三选一起点：都不传 = 从头；copy_from_option_id = 照抄本产品某选项的 overrides；copy_from_product_id = 把那个产品默认选项的 effective 值写成新选项的 overrides */
  copy_from_option_id?: string;
  copy_from_product_id?: string;
}

export interface PatchOptionBody {
  name_cn?: string;
  name_en?: string;
  status?: OptionStatus;
  sort_order?: number;
  /** TICKETOPT-1 · 门票选项游玩时长（分钟 1–1440；null = 不填） */
  visit_minutes?: number | null;
  /** 只提交传了的键；null = 恢复跟随产品 */
  overrides?: Partial<OptionOverrides>;
  /** REASON-2 · 改价格类覆盖（餐标 / 车队日成本 / 城际段）真变了必带原因（price_change） */
  reason_code?: string;
  reason?: string;
}

const base = (productId: string) => `/api/v1/products/${productId}/options`;

export const productOptionsApi = {
  list: (productId: string) => api<ProductOption[]>(base(productId)),
  create: (productId: string, body: CreateOptionBody) =>
    api<ProductOption>(base(productId), { method: "POST", body: JSON.stringify(body) }),
  patch: (productId: string, optionId: string, body: PatchOptionBody) =>
    api<ProductOption>(`${base(productId)}/${optionId}`, { method: "PATCH", body: JSON.stringify(body) }),
  setDefault: (productId: string, optionId: string) =>
    api<ProductOption>(`${base(productId)}/${optionId}:set-default`, { method: "POST", body: JSON.stringify({}) }),
};

export const optionPricingApi = {
  get: (productId: string, optionId: string) => api<OptionPricingView>(`${base(productId)}/${optionId}/pricing`),
  /** 低于成本无说明 422；默认选项 409；没变化 changed=false */
  save: (productId: string, optionId: string, body: SaveOptionPricingBody) =>
    api<{ changed: boolean; below_cost?: boolean; pricing: OptionPricingView }>(`${base(productId)}/${optionId}/pricing`, { method: "PUT", body: JSON.stringify(body) }),
};

export const optionQueryKey = (productId: string) => ["products", productId, "options"] as const;

/** 选项里「单独设」的成本项（卡片用）：返回中文名列表，没有 = 全部跟随产品。 */
export function overriddenCostItems(o: Pick<ProductOption, "overrides">): string[] {
  const out: string[] = [];
  if (o.overrides.fleet_daily_cost_override_cents != null) out.push("车队成本");
  if (o.overrides.lunch_cost_per_person_cents != null) out.push("午餐餐标");
  if (o.overrides.included_tickets != null) out.push("已含门票");
  if (o.overrides.intercity_legs != null) out.push("城际用车");
  return out;
}

/** 平台连接汇总：{gyg: 2 条有效映射, ...}；只数 active。 */
export function activeMappingsByPlatform(mappings: OptionMapping[]): Record<string, number> {
  const m: Record<string, number> = {};
  for (const x of mappings) if (x.status === "active") m[x.platform_code] = (m[x.platform_code] ?? 0) + 1;
  return m;
}
